import React, { useContext, useEffect, useState } from 'react';
import {
  Dimensions,
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  TouchableHighlight,
  View,
} from 'react-native';
import DyteClient, {
  DyteParticipant,
  DytePlugin,
  DyteSelf,
} from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import PeerView from '../PeerView';
import DytePluginMain from './DytePluginMain';
import DyteScreenshareView from './DyteScreenshareView';
import DyteText from './DyteText';
import { SvgXml } from 'react-native-svg';
import DyteNameTag from './DyteNameTag';
import { IconPack, defaultIconPack } from '../lib/icons';
import DyteParticipantTile from './DyteParticipantTile';
import { Peer } from '../types/dyte-client';
import { DyteI18n, useLanguage } from '../lib/lang';
import { DyteUIContext } from '../lib/context-provider/ui-context';

interface PluginParticipant {
  data: DytePlugin;
  type: string;
}

interface MeetingParticipant {
  data: DyteParticipant | DyteSelf;
  type: string;
}

type SelectedParticipant = PluginParticipant | MeetingParticipant;

enum TabChangeSource {
  User,
  Meeting,
}

export default function DyteHighlightedGrid({
  meeting,
  participants,
  pinnedParticipants,
  screenshareParticipants,
  plugins,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  aspectRatio = '3:4',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  gap = 4,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  size = 'sm',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  variant = 'solid',
  iconPack = defaultIconPack,
  t,
}: {
  meeting: DyteClient;
  participants: Peer[];
  pinnedParticipants: Peer[];
  screenshareParticipants: Peer[];
  plugins: DytePlugin[];
  aspectRatio?: string;
  gap?: number;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  variant?: 'boxed' | 'solid';
  iconPack?: IconPack;
  t?: DyteI18n;
}) {
  const defaultLang = useLanguage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const [syncTab, setSync] = useState(true);
  const broadcastTabChanges = useDyteSelector(
    (m) => m.meta.broadcastTabChanges
  );
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const activePlugins = plugins;
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [highlightDimensions, setHighlightDimensions] = useState({
    width: 0,
    height: 0,
  });
  const peerWidth = dimensions.width / 2 - 4;
  const selfScreenShare = meeting.self.screenShareEnabled ? meeting.self : null;
  const getIntialSelectedParticipant = () => {
    if (activePlugins.length > 0) {
      return {
        data: activePlugins[0],
        type: 'plugin',
      };
    } else if (screenshareParticipants.length > 0) {
      return {
        data: screenshareParticipants[0],
        type:
          meeting.meta.viewType === 'LIVESTREAM'
            ? meeting.livestream.state === 'LIVESTREAMING' &&
              meeting.stage.status === 'OFF_STAGE'
              ? 'livestream'
              : 'none'
            : 'screenshare',
      };
    } else if (pinnedParticipants.length > 0) {
      return {
        data: pinnedParticipants[0],
        type: 'pinned',
      };
    } else {
      return {
        data: meeting.self,
        type: 'pinned',
      };
    }
  };
  const initialParticipant = getIntialSelectedParticipant();
  const [selectedParticipant, setSelectedParticipant] =
    useState<SelectedParticipant>(initialParticipant);
  const onlyPinnedParticipant =
    activePlugins.length === 0 && screenshareParticipants.length === 0;
  const onStage = participants;
  const closeFullScreen = () => {
    return setStates({ ...storeStates, activeFullscreen: false });
  };
  useEffect(() => {
    if (onlyPinnedParticipant && pinnedParticipants.length > 0)
      setSelectedParticipant({
        data: pinnedParticipants[0],
        type: 'pinned',
      });
  }, [onlyPinnedParticipant, pinnedParticipants]);
  useEffect(() => {
    if (!onlyPinnedParticipant)
      setSelectedParticipant(
        activePlugins.length > 0
          ? {
              data: activePlugins[0],
              type: 'plugin',
            }
          : {
              data: screenshareParticipants[0],
              type: 'screenshare',
            }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyPinnedParticipant]);
  const visible = storeStates.activeFullscreen;
  useEffect(() => {
    const setTab = (tab) => {
      const screenshareTab = screenshareParticipants.filter(
        (p) => p.id === tab.id
      )[0];
      const pluginTab = meeting.plugins.all
        .toArray()
        .filter((p) => p.id === tab.id)[0];
      if (tab.type === 'screenshare') {
        setSelectedParticipant({
          data: screenshareTab,
          type: 'screenshare',
        });
      } else if (tab.type === 'plugin') {
        setSelectedParticipant({
          data: pluginTab as DytePlugin,
          type: 'plugin',
        });
      }
    };
    meeting.meta.addListener('activeTabUpdate', (ev) => {
      if (syncTab) setTab(ev);
    });
    return () => {
      meeting.meta.removeListener('activeTabUpdate', (ev) => {
        if (syncTab) setTab(ev);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting]);
  useEffect(() => {
    if (syncTab) {
      setSelectedParticipant({
        data: selectedParticipant.data as any,
        type: selectedParticipant.type,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { width, height } = Dimensions.get('screen');
  const styles = StyleSheet.create({
    highlightStyle: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background[1000],
    },
    participantSelector: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginHorizontal: 4,
      padding: 8,
      borderRadius: 8,
    },
    scrollStyle: {
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'row',
      padding: 4,
      backgroundColor: colors.background[1000],
    },
    bottomLeftContainer: {
      position: 'absolute',
      bottom: 10,
      left: 10,
    },
    topLeftContainer: {
      position: 'absolute',
      top: 10,
      left: 10,
    },
    iconStyle: {
      padding: 4,
    },
    image: {
      height: 24,
      width: 24,
      marginRight: 15,
    },
    fullScreenContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 8,
      backgroundColor: colors.background[900],
    },
    fullscreen: {
      width: height * 0.95,
      height: width * 0.95,
      justifyContent: 'center',
      alignItems: 'center',
      transform: [{ rotate: '90deg' }],
    },
  });
  if (!selectedParticipant.data && meeting.meta.viewType !== 'LIVESTREAM')
    return <></>;
  else if (!selectedParticipant.data) return <></>;
  else {
    return (
      // eslint-disable-next-line react-native/no-inline-styles
      <View style={{ flex: 1 }}>
        <View
          style={styles.scrollStyle}
          onLayout={(event) => {
            setDimensions({
              width: event.nativeEvent.layout.width,
              height: event.nativeEvent.layout.height,
            });
          }}
        >
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {meeting.self.permissions.canSpotlight &&
              selectedParticipant.type !== 'pinned' && (
                <TouchableHighlight
                  underlayColor={colors.background[800]}
                  onPress={() => {
                    setSync(!broadcastTabChanges);
                    meeting.meta.setBroadcastTabChanges(!broadcastTabChanges);
                  }}
                >
                  <View
                    style={{
                      ...styles.participantSelector,
                      backgroundColor: syncTab
                        ? colors.success
                        : colors.background[900],
                    }}
                  >
                    {/* eslint-disable-next-line react-native/no-inline-styles */}
                    <DyteText style={{ marginRight: 4 }}>Sync</DyteText>
                    <SvgXml
                      xml={
                        syncTab
                          ? iconPack.checkmark.replace(
                              'currentColor',
                              colors.text
                            )
                          : iconPack.warning.replace(
                              'currentColor',
                              colors.danger
                            )
                      }
                      width={25}
                      height={25}
                    />
                  </View>
                </TouchableHighlight>
              )}
            {activePlugins.map((p, index) => (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                key={index}
                onPress={() => {
                  setSelectedParticipant({
                    data: p,
                    type: 'plugin',
                  });
                  meeting.meta.setSelfActiveTab(
                    {
                      id: p.id,
                      type: 'plugin',
                    },
                    TabChangeSource.User
                  );
                }}
              >
                <View
                  style={{
                    ...styles.participantSelector,
                    backgroundColor:
                      selectedParticipant.type === 'plugin' &&
                      selectedParticipant.data.id === p.id
                        ? colors.brand[500]
                        : colors.background[900],
                  }}
                >
                  <Image style={styles.image} source={{ uri: p.picture }} />
                  <DyteText>{p.name}</DyteText>
                </View>
              </TouchableHighlight>
            ))}
            {screenshareParticipants.map((s, index) => (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                key={index}
                onPress={() => {
                  setSelectedParticipant({
                    data: s,
                    type: 'screenshare',
                  });
                  meeting.meta.setSelfActiveTab(
                    {
                      id: s.id,
                      type: 'screenshare',
                    },
                    TabChangeSource.User
                  );
                }}
              >
                <View
                  style={{
                    ...styles.participantSelector,
                    backgroundColor:
                      selectedParticipant.type === 'screenshare' &&
                      selectedParticipant.data.id === s.id
                        ? colors.brand[500]
                        : colors.background[900],
                  }}
                >
                  <View style={styles.iconStyle}>
                    <SvgXml
                      xml={iconPack.share_screen_person.replace(
                        'currentColor',
                        colors.text
                      )}
                      width={16}
                      height={16}
                    />
                  </View>
                  <DyteText>{s.name}</DyteText>
                </View>
              </TouchableHighlight>
            ))}
          </ScrollView>
        </View>
        <View
          style={styles.highlightStyle}
          onLayout={(event) => {
            setHighlightDimensions({
              width: event.nativeEvent.layout.width,
              height: event.nativeEvent.layout.height,
            });
          }}
        >
          {selectedParticipant.type === 'screenshare' && (
            <DyteScreenshareView
              meeting={meeting}
              participant={selectedParticipant.data as DyteParticipant}
            />
          )}

          {selectedParticipant.type === 'plugin' && (
            <DytePluginMain
              meeting={meeting}
              plugin={selectedParticipant.data as DytePlugin}
            />
          )}

          {selectedParticipant.type === 'pinned' && (
            <DyteParticipantTile
              meeting={meeting}
              participant={selectedParticipant.data as DyteParticipant}
              // eslint-disable-next-line react-native/no-inline-styles
              style={{
                width: highlightDimensions.width * 0.95,
                height: highlightDimensions.height * 0.95,
                borderRadius: 8,
              }}
            />
          )}
        </View>
        {selectedParticipant.type === 'screenshare' && (
          <Modal transparent visible={visible} onRequestClose={closeFullScreen}>
            <View style={styles.fullScreenContainer}>
              <View style={styles.fullscreen}>
                <DyteScreenshareView
                  meeting={meeting}
                  participant={selectedParticipant.data as DyteParticipant}
                />
              </View>
            </View>
          </Modal>
        )}
        {selectedParticipant.type === 'plugin' && (
          <Modal transparent visible={visible} onRequestClose={closeFullScreen}>
            <View style={styles.fullScreenContainer}>
              <View style={styles.fullscreen}>
                <DytePluginMain
                  meeting={meeting}
                  plugin={selectedParticipant.data as DytePlugin}
                />
              </View>
            </View>
          </Modal>
        )}
        {meeting.meta.viewType === 'GROUP_CALL' && (
          <View
            style={styles.scrollStyle}
            onLayout={(event) => {
              setDimensions({
                width: event.nativeEvent.layout.width,
                height: event.nativeEvent.layout.height,
              });
            }}
          >
            <ScrollView horizontal>
              {pinnedParticipants.length > 0 &&
                pinnedParticipants
                  .filter((p) => {
                    const flag =
                      activePlugins.length === 0 &&
                      selfScreenShare === null &&
                      screenshareParticipants.length === 0;
                    if (flag) return p.id !== pinnedParticipants[0].id;
                    else return true;
                  })
                  .map((p, index) => {
                    return (
                      // eslint-disable-next-line react-native/no-inline-styles
                      <View style={{ margin: 4 }} key={index}>
                        <PeerView
                          participant={p}
                          width={peerWidth}
                          height={peerWidth * (4 / 3)}
                          isScreenshare={false}
                        />
                        {p.isPinned && (
                          <View style={styles.topLeftContainer}>
                            <View style={styles.iconStyle}>
                              <SvgXml
                                xml={iconPack.pin.replace(
                                  'currentColor',
                                  colors.text
                                )}
                                width={20}
                                height={20}
                              />
                            </View>
                          </View>
                        )}
                        <View style={styles.bottomLeftContainer}>
                          <DyteNameTag meeting={meeting} participant={p}>
                            {p.audioEnabled ? (
                              <View style={styles.iconStyle}>
                                <SvgXml
                                  xml={iconPack.mic_on.replace(
                                    'currentColor',
                                    colors.text
                                  )}
                                  width={16}
                                  height={16}
                                />
                              </View>
                            ) : (
                              /* <DyteAudioVisualizer participant={participant} />*/
                              <View style={styles.iconStyle}>
                                <SvgXml
                                  xml={iconPack.mic_off.replace(
                                    'currentColor',
                                    colors.danger
                                  )}
                                  width={16}
                                  height={16}
                                />
                              </View>
                            )}
                          </DyteNameTag>
                        </View>
                      </View>
                    );
                  })}
              {meeting !== undefined &&
                participants
                  .filter((actP) => !actP.isPinned)
                  .map((p, index) => (
                    // eslint-disable-next-line react-native/no-inline-styles
                    <View style={{ margin: 4 }} key={index}>
                      <PeerView
                        participant={p}
                        width={peerWidth}
                        height={peerWidth * (4 / 3)}
                        isScreenshare={false}
                      />
                      {p.isPinned && (
                        <View style={styles.topLeftContainer}>
                          <View style={styles.iconStyle}>
                            <SvgXml
                              xml={iconPack.pin.replace(
                                'currentColor',
                                colors.text
                              )}
                              width={20}
                              height={20}
                            />
                          </View>
                        </View>
                      )}
                      <View style={styles.bottomLeftContainer}>
                        <DyteNameTag meeting={meeting} participant={p}>
                          {p.audioEnabled ? (
                            <View style={styles.iconStyle}>
                              <SvgXml
                                xml={iconPack.mic_on.replace(
                                  'currentColor',
                                  colors.text
                                )}
                                width={16}
                                height={16}
                              />
                            </View>
                          ) : (
                            /* <DyteAudioVisualizer participant={participant} />*/
                            <View style={styles.iconStyle}>
                              <SvgXml
                                xml={iconPack.mic_off.replace(
                                  'currentColor',
                                  colors.danger
                                )}
                                width={16}
                                height={16}
                              />
                            </View>
                          )}
                        </DyteNameTag>
                      </View>
                    </View>
                  ))}
            </ScrollView>
          </View>
        )}
        {(meeting.meta.viewType === 'WEBINAR' ||
          meeting.meta.viewType === 'LIVESTREAM') && (
          <View
            style={styles.scrollStyle}
            onLayout={(event) => {
              setDimensions({
                width: event.nativeEvent.layout.width,
                height: event.nativeEvent.layout.height,
              });
            }}
          >
            <ScrollView horizontal>
              {pinnedParticipants.length > 0 &&
                pinnedParticipants
                  .filter((p) => {
                    const flag =
                      activePlugins.length === 0 &&
                      selfScreenShare === null &&
                      screenshareParticipants.length === 0;

                    if (
                      flag &&
                      (meeting.self.isPinned ||
                        selectedParticipant.type === 'pinned')
                    ) {
                      return p.id !== selectedParticipant.data.id;
                    } else {
                      return true;
                    }
                  })
                  .map((p, index) => {
                    return (
                      // eslint-disable-next-line react-native/no-inline-styles
                      <View style={{ margin: 4 }} key={index}>
                        <PeerView
                          participant={p}
                          width={peerWidth}
                          height={peerWidth * (4 / 3)}
                          isScreenshare={false}
                        />
                        {p.isPinned && (
                          <View style={styles.topLeftContainer}>
                            <View style={styles.iconStyle}>
                              <SvgXml
                                xml={iconPack.pin.replace(
                                  'currentColor',
                                  colors.text
                                )}
                                width={20}
                                height={20}
                              />
                            </View>
                          </View>
                        )}
                        <View style={styles.bottomLeftContainer}>
                          <DyteNameTag meeting={meeting} participant={p}>
                            {p.audioEnabled ? (
                              <View style={styles.iconStyle}>
                                <SvgXml
                                  xml={iconPack.mic_on.replace(
                                    'currentColor',
                                    colors.text
                                  )}
                                  width={16}
                                  height={16}
                                />
                              </View>
                            ) : (
                              /* <DyteAudioVisualizer participant={participant} />*/
                              <View style={styles.iconStyle}>
                                <SvgXml
                                  xml={iconPack.mic_off.replace(
                                    'currentColor',
                                    colors.danger
                                  )}
                                  width={16}
                                  height={16}
                                />
                              </View>
                            )}
                          </DyteNameTag>
                        </View>
                      </View>
                    );
                  })}
              {meeting !== undefined &&
                onStage
                  .filter((actP) => !actP.isPinned)
                  .map((p, index) => (
                    // eslint-disable-next-line react-native/no-inline-styles
                    <View style={{ margin: 4 }} key={index}>
                      <PeerView
                        participant={p}
                        width={peerWidth}
                        height={peerWidth * (4 / 3)}
                        isScreenshare={false}
                      />
                      {p.isPinned && (
                        <View style={styles.topLeftContainer}>
                          <View style={styles.iconStyle}>
                            <SvgXml
                              xml={iconPack.pin.replace(
                                'currentColor',
                                colors.text
                              )}
                              width={20}
                              height={20}
                            />
                          </View>
                        </View>
                      )}
                      <View style={styles.bottomLeftContainer}>
                        <DyteNameTag meeting={meeting} participant={p}>
                          {p.audioEnabled ? (
                            <View style={styles.iconStyle}>
                              <SvgXml
                                xml={iconPack.mic_on.replace(
                                  'currentColor',
                                  colors.text
                                )}
                                width={16}
                                height={16}
                              />
                            </View>
                          ) : (
                            /* <DyteAudioVisualizer participant={participant} />*/
                            <View style={styles.iconStyle}>
                              <SvgXml
                                xml={iconPack.mic_off.replace(
                                  'currentColor',
                                  colors.danger
                                )}
                                width={16}
                                height={16}
                              />
                            </View>
                          )}
                        </DyteNameTag>
                      </View>
                    </View>
                  ))}
            </ScrollView>
          </View>
        )}
      </View>
    );
  }
}
